<template>
  <el-form
    class="category-form"
    ref="form"
    :model="model"
    :rules="rules"
  >
    <!-- Uncollapsed state -->
    <template v-if="!isCollapsed">
      <el-row :gutter="gutter">
        <el-col
          class="category-form__header"
          :lg="24" :md="24" :sm="24" :xs="24"
        >
          <app-form-subtitle
            class="category-form__subtitle"
            :caption="categoryFormSubtitle"
          />
          <div class="category-form__controls">
            <i
              class="category-form__delete icon-quit"
              @click.prevent.stop="showConfirmationAlert"
            />
          </div>
        </el-col>

        <el-col :lg="24" :md="24" :sm="24" :xs="24">
          <el-form-item
            class="el-form-item__radio"
            :label="fields.gender.label"
            :prop="fields.gender.name"
            :required="fields.gender.required"
          >
            <el-radio
              v-for="option in fields.gender.options"
              :key="option.id"
              :label="option.id"
              v-model="gender"
              @change="$refs.form.validateField(fields.gender.name)"
              :disabled="!hasAgeCategoriesLeft(option.id)"
            >
              <span v-text="option.label" />
            </el-radio>
          </el-form-item>
        </el-col>

        <el-col :lg="12" :md="12" :sm="12" :xs="24">
          <el-form-item
            class="el-form-item__radio"
            :label="fields.format.label"
            :prop="fields.format.name"
            :required="fields.format.required"
          >
            <el-radio
              v-for="option in fields.format.options"
              :key="option.id"
              :label="option.id"
              v-model="format"
              @change="$refs.form.validateField(fields.format.name)"
              :disabled="!hasAgeCategoriesLeft(gender, option.id)"
            >
              <span v-text="option.label" />
            </el-radio>
          </el-form-item>
        </el-col>

        <el-col :lg="12" :md="12" :sm="12" :xs="24">
          <el-form-item
            :label="fields.ageCategory.label"
            :prop="fields.ageCategory.name"
            :required="fields.ageCategory.required"
            ref="ageCategoryField"
          >
            <el-select
              :placeholder="i18n('entities.clubCategory.placeholders.ageCategory')"
              v-model="ageCategory"
              :disabled="!ageCategoriesAvailable.length"
            >
              <i
                slot="prefix"
                class="icon-training-element-1"
              />
              <el-option
                v-for="option in ageCategoriesEnabled"
                :key="`age-categories--${format}--${option.id}`"
                :label="option.label"
                :value="option.id"
                :disabled="option.disabled"
                v-show="!option.hidden"
              />
            </el-select>
          </el-form-item>
        </el-col>

        <el-col
          v-if="model.format"
          :lg="24" :md="24" :sm="24" :xs="24"
        >
          <el-form-item
            class="el-form-item__checkbox"
            :prop="fields.minimumPlayersCommitment.name"
            :required="fields.minimumPlayersCommitment.required"
            ref="minimumPlayersCommitmentField"
          >
            <el-checkbox v-model="model[fields.minimumPlayersCommitment.name]">
              <app-i18n
                code="entities.clubCategory.fields.minimumPlayersCommitment"
                :args="[minimumPlayersByFormat]"
              />
            </el-checkbox>
          </el-form-item>
        </el-col>

        <el-col
          class="category-form__multiple-teams"
          :lg="12" :md="12" :sm="12" :xs="24"
        >
          <el-form-item
            class="el-form-item__radio"
            :label="fields.multipleTeams.label"
            :prop="fields.multipleTeams.name"
            :required="fields.multipleTeams.required"
          >
            <el-radio
              v-for="option in fields.multipleTeams.options"
              :key="option.id"
              :label="option.id"
              v-model="multipleTeams"
              @change="$refs.form.validateField(fields.multipleTeams.name)"
            >
              <span v-text="option.label" />
            </el-radio>
          </el-form-item>
        </el-col>

        <el-col
          v-if="!multipleTeams"
          class="category-form__teams__button category-form__save"
          :lg="12" :md="12" :sm="12" :xs="24"
        >
          <el-button
            type="primary"
            class="category-form__teams__button category-form__save__button"
            @click="validateAndCollapse()"
          >
            <app-i18n code="common.save" />
          </el-button>
        </el-col>
      </el-row>

      <el-row
        v-if="multipleTeams"
        class="category-form__teams"
      >
        <el-col
          class="category-form__teams__forms"
          :lg="24" :md="24" :sm="24" :xs="24"
        >
          <el-form-item
            class="category-form__teams__forms__form"
            v-for="(team, i) in teams"
            :key="`category--${index}-team--${i}`"
            :label="`${i18n('teamRegistrationSite.form.step4.labels.team')} ${i + 1}`"
            :rules="teamRules"
            :prop="`teams.${i}`"
            required
          >
            <el-input
              v-model="model.teams[i]"
              :name="`teams.${i}`"
              class="category-form__teams__forms__form__input"
              :placeholder="i18n('entities.clubCategory.placeholders.teamName')"
              prefix-icon="icon-player"
            >
              <template slot="prepend">
                <div class="category-form__teams__forms__form__input__prepend">
                  <i class="icon-teams" />
                  <span v-html="teamCategoryNameHtml" />
                </div>
              </template>
            </el-input>
            <i
              class="category-form__teams__forms__form__remove icon-quit"
              @click="removeTeam(i)"
            />
          </el-form-item>
        </el-col>

        <el-col
          class="category-form__teams__button category-form__teams__button--add"
          :class="{ 'category-form__teams__button--hide': !teams || teams.length >= maxTeamsPerCategory }"
          :lg="12" :md="12" :sm="12" :xs="24"
        >
          <el-button
            v-if="teams && teams.length < maxTeamsPerCategory"
            class="category-form__teams__button__el"
            type="secondary"
            @click="addTeam"
          >
            <span class="category-form__teams__button__el__caption">
              <i class="icon-add" />
              <app-i18n code="teamRegistrationSite.form.step4.buttons.addTeam" />
            </span>
          </el-button>
          <span
            v-else
            v-html="'&nbsp;'"
          />
        </el-col>

        <el-col
          class="category-form__teams__button category-form__teams__button--save"
          :lg="12" :md="12" :sm="12" :xs="24"
        >
          <el-button
            type="primary"
            @click="validateAndCollapse()"
          >
            <app-i18n code="common.save" />
          </el-button>
        </el-col>
      </el-row>
    </template>

    <!-- Collapsed state -->
    <template v-else>
      <el-row :gutter="gutter * 4">
        <el-col
          class="category-form__header"
          :lg="24" :md="24" :sm="24" :xs="24"
        >
          <app-form-subtitle
            class="category-form__subtitle"
            :caption="categoryFormSubtitle"
          />
          <div class="category-form__controls">
            <span
              class="category-form__edit"
              @click.prevent.stop="uncollapse"
            >
              <i class="category-form__edit__icon icon-pen" />
            </span>
            <i
              class="category-form__delete icon-quit"
              @click.prevent.stop="showConfirmationAlert"
            />
          </div>
        </el-col>
      </el-row>
    </template>
    
    <app-custom-alert
      :visible.sync="confirmationAlertVisible"
      closeable
      type="delete"
      :confirm-text="i18n('common.yes')"
      :title="i18n('teamRegistrationSite.form.step4.confirm')"
      :on-confirm-fn="() => $emit('delete')"
    />
  </el-form>
</template>

<script>
import _ from 'lodash'

import AppFormSubtitle from '@/components/FormSubtitle.vue'

import { i18n } from '@shared/i18n'
import { FormSchema } from '@shared/form/form-schema'

import { ClubCategoryModel } from '@/models/club-category-model'
import { mapState, mapGetters } from 'vuex'

const { fields } = ClubCategoryModel

const formSchema = new FormSchema([
  fields.gender,
  fields.format,
  fields.ageCategory,
  fields.minimumPlayersCommitment,
  fields.multipleTeams,
  fields.teams,
])

const generateModelComputed = key => ({
  get() {
    return this.model[key]
  },
  set(val) {
    this.model = {
      ...this.model,
      [key]: val,
    }
  },
})

export default {
  name: 'appCategoryForm',
  props: {
    value: Object,
    index: Number,
    gutter: Number,
    isRemovingCategories: Boolean,
  },
  data() {
    return {
      i18n,
      fields,
      rules: formSchema.rules(),
      teamRules: [{
        required: true,
        message: i18n('validation.mixed.required'),
      }],
      model: {},
      isCollapsed: false,
      maxTeamsPerCategory: 5,
      confirmationAlertVisible: false,
    }
  },
  computed: {
    ...mapState('tournament', ['ageCategories']),
    ...mapGetters('teamRegistration', ['clubName', 'clubCategoriesInUseByGenderAndFormat']),

    gender: generateModelComputed('gender'),
    format: generateModelComputed('format'),
    ageCategory: generateModelComputed('ageCategory'),
    multipleTeams: generateModelComputed('multipleTeams'),
    teams: generateModelComputed('teams'),

    hasAgeCategoriesLeft() {
      return (gender, format) => {
        if (!format) {
          const filterOutCategories = this.clubCategoriesInUseByGenderAndFormat[gender] || []
          return this.ageCategoriesByGender[gender].length > Object.values(filterOutCategories).flat().length
        }

        if (!gender) return false

        const filterOutCategories = this.clubCategoriesInUseByGenderAndFormat[gender][format] || []
        const ageCategoriesByFormatAndGender = this.ageCategoriesByFormatAndGender[format][gender]
        return !ageCategoriesByFormatAndGender || ageCategoriesByFormatAndGender.length > filterOutCategories.length
      }
    },
    categoryFormSubtitle() {
      const i18nTitle = i18n('teamRegistrationSite.form.step4.subtitles.category')
      const index = this.index + 1

      const { gender, format, ageCategory } = this.model

      if (!gender || !format || !ageCategory) return `${i18nTitle} ${index}`
      
      const i18nGender = i18n(`enumerators.gender.${gender}`)
      const i18nFormat = i18n(`enumerators.soccerFormat.${format}`)
      const i18nAgeCategory = this.ageCategories.find(c => c.id === ageCategory).label
      
      return `${i18nGender} - ${i18nFormat} - ${i18nAgeCategory}`
    },
    teamCategoryNameHtml() {
      return `${this.clubName}<br />${this.categoryFormSubtitle}`
    },
    ageCategoriesByGender() {
      return this.ageCategories.reduce((result, category) => {
        const { genders } = category

        for (const gender of genders) {
          if (!result[gender]) result[gender] = []
          result[gender].push(category.id)
        }

        return result 
      }, {})
    },
    ageCategoriesByFormatAndGender() {
      return this.ageCategories.reduce((result, category) => {
        const { format, genders } = category
        
        if (!result[format]) result[format] = {}

        for (const gender of genders) {
          if (!result[format][gender]) result[format][gender] = []
          result[format][gender].push(category.id)
        }

        return result 
      }, {})
    },
    ageCategoriesAvailable() {
      if (!this.model.format) return []
      return this.ageCategories.filter(c => {
        const { gender, format } = this.model
        if (!gender) return false
        if (!c.genders.includes(gender)) return false
        const filterOutCategories = this.clubCategoriesInUseByGenderAndFormat[gender][format] || []
        if (filterOutCategories.includes(c.id)) return false
        return c.format === this.model.format
      }).map(c => ({
        id: c.id,
        label: c.label,
      }))
    },
    ageCategoriesEnabled() {
      return this.ageCategories.map(c => {
        const result = {
          id: c.id,
          label: c.label,
          disabled: false,
          hidden: false,
        }

        const { gender, format } = this.model

        if (!c.genders.includes(gender)) {
          result.hidden = true
          result.disabled = true
        } else if (format !== c.format) {
          result.hidden = true
          result.disabled = true
        } else {
          const filterOutCategories = this.clubCategoriesInUseByGenderAndFormat[gender][format] || []
          if (filterOutCategories.includes(c.id)) result.disabled = true
        }

        return result
      })
    },
    minimumPlayersByFormat() {
      if (!this.model) return null
      switch (this.model.format) {
        case 'f7': return 11;
        case 'f11': return 16;
        default: return null;
      }
    },
  },
  async mounted() {
    await this.$nextTick()
    this.validateAndCollapse(true)
  },
  methods: {
    async validateAndCollapse(clearValidation = false) {
      if (!this.$refs.form) return

      try {
        await this.$refs.form.validate()
        this.isCollapsed = true
      } catch (e) {
        // 
      } finally {
        if (clearValidation) this.$refs.form.clearValidate()
      }
    },
    uncollapse() {
      this.isCollapsed = false
    },
    addTeam() {
      if (this.teams.length >= this.maxTeamsPerCategory) return
      this.teams = [...this.teams, null]
    },
    removeTeam(i) {
      if (this.teams.length <= 2) {
        this.multipleTeams = false
        this.teams = null
        return
      }

      const teams = [...this.teams]
      teams.splice(i, 1)
      this.teams = teams
    },
    showConfirmationAlert() {
      this.confirmationAlertVisible = true
    },
  },
  watch: {
    value: {
      async handler(val, oldVal) {
        if (!val) return
        if (_.isEqual(val, oldVal)) return
        this.model = val
        await this.$nextTick()
      },
      deep: true,
      immediate: true,
    },
    model: {
      handler(val, oldVal) {
        if (_.isEqual(val, oldVal)) return
        this.$emit('input', val)
      },
      deep: true,
      immediate: true,
    },
    async 'model.gender'() {
      if (this.isRemovingCategories) return

      this.model = {
        ...this.model,
        format: null,
      }
    },
    async 'model.format'() {
      if (this.isRemovingCategories) return

      this.model = {
        ...this.model,
        ageCategory: null,
        minimumPlayersCommitment: null,
      }

      await this.$nextTick()

      if (this.$refs.minimumPlayersCommitmentField) this.$refs.minimumPlayersCommitmentField.clearValidate()
      if (this.$refs.ageCategoryField) this.$refs.ageCategoryField.clearValidate()
    },
    async 'model.multipleTeams'(val) {
      if (this.isRemovingCategories) return

      if (!val) return this.model = {
        ...this.model,
        teams: [],
      }

      if (this.model.teams && this.model.teams.length) return

      this.model = {
        ...this.model,
        teams: [null, null],
      }
    },
  },
  components: {
    AppFormSubtitle,
  },
}
</script>

<style lang="scss" scoped>
.category-form {
  &__save {
    display: flex;
    height: 60px;
    margin-top: 16px;

    @include tablet {
      display: flex;
      height: 80px;
      align-items: center;
      justify-content: flex-end;
      margin-top: 0;
      margin-bottom: 10px;
    }

    &__button {
      flex-grow: 1;
      justify-content: center;
      margin: 8px 0 0;

      @include tablet {
        flex-grow: initial;
        margin: 0;
      }
    }
  }

  &__header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 8px;
    align-items: center;

    @include tablet {
      padding: 16px 8px;
      flex-wrap: initial;
    }
  }

  &__multiple-teams {
    height: 80px;
  }

  &__teams {
    margin-bottom: 32px;

    &__forms {
      margin-top: 16px;
      margin-bottom: 8px;

      @include tablet {
        margin-top: 0;
        margin-bottom: 16px;
      }

      .el-form-item {
        margin-bottom: 16px !important;
      }

      &__form {
        &__input {
          &__prepend {
            display: flex;
            align-items: center;

            i {
              margin-right: 8px;
            }

            span {
              font-size: 10px;
              white-space: pre-wrap;

              @include tablet {
                font-size: 13px;
              }
            }
          }
        }

        &__remove {
          cursor: pointer;
          font-size: 18px;
          color: #c5c5c5;
          margin-left: 8px;
        }
      }
    }

    &__button {
      display: flex;
      align-items: center;

      &--hide {
        display: none;

        @include tablet {
          display: flex;
        }
      }

      &__el {
        &__caption {
          display: flex;
          justify-content: center;

          i {
            margin-right: 8px;
          }
        }
      }

      &--add {
        .el-button {
          flex-grow: 1;
          margin-bottom: 16px;

          @include tablet {
            margin-bottom: 0;
          }
        }
      }

      &--save {
        justify-content: flex-end;

        .el-button {
          flex-grow: 1;

          @include tablet {
            flex-grow: initial;
          }
        }
      }
    }
  }

  &__subtitle {
    margin: 2px 0 0;
    align-self: flex-start;
    font-size: 11px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 80%;

    @include mobile {
      font-size: 12px;
    }

    @include tablet {
      font-size: 14px;
    }
  }

  &__controls {
    display: flex;
    align-items: center;
  }

  &__edit {
    cursor: pointer;
    display: flex;
    align-items: center;

    &__icon {
      color: #2acccc;
      margin-right: 8px;
      font-size: 16px;

      @include mobile {
        font-size: 20px;
      }

      @include tablet {
        font-size: 14px;
      }
    }
  }

  &__delete {
    cursor: pointer;
    font-size: 16px;

    @include mobile {
      font-size: 20px;
    }

    @include tablet {
      font-size: 14px;
    }
  }

  .el-radio {
    display: flex;
    margin-top: 4px;
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: normal;
    align-items: center;
    align-content: center;

    @include tablet {
      margin-top: 0;
      margin-bottom: 0;
      display: inline-flex;
    }
  }
}
</style>

<style lang="scss">
.category-form {
  .el-form-item__radio,
  .el-form-item__checkbox {
    .el-form-item__label {
      padding: 0 8px 3px;
    }

    .el-form-item__content {
      padding: 0 8px;

      .el-form-item__error {
        position: absolute;
        left: 8px;
        margin-top: -8px;

        @include tablet {
          margin-top: 0;
        }
      }
    }
  }

  &__teams {
    &__forms {
      &__form {
        .el-form-item__content {
          display: flex;
          align-items: center;
        }
      }
    }
  }

  .el-checkbox {
    line-height: 1.2;
    display: flex;
    align-items: flex-start;

    @include tablet {
      align-items: center;
    }

    &__label {
      white-space: pre-wrap;
    }
  }

  .el-input-group__prepend {
    padding: 0 8px;
    max-width: 130px;
    width: 130px;

    @include tablet {
      padding: 0 16px;
      max-width: 100%;
      width: auto;
    }
  }
}
</style>